import React, { useState } from "react";
import { useUpdatePassword } from "@/hooks/use-update-password";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * VIB-634: skjema som vises etter at ansatt har klikket på reset-lenken i
 * mail og Supabase har sendt PASSWORD_RECOVERY-eventet. Selve auth-kallet
 * ligger i useUpdatePassword-hooken slik at komponenten ikke importerer
 * supabase-klienten direkte (VIB-647).
 */
const MIN_PASSWORD_LENGTH = 8;

export function ResetPasswordForm() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const updatePassword = useUpdatePassword();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Passord må være minst ${MIN_PASSWORD_LENGTH} tegn`);
      return;
    }
    if (password !== confirm) {
      setError("Passordene matcher ikke");
      return;
    }

    try {
      await updatePassword.mutateAsync(password);
      // Suksess: AuthContext sin USER_UPDATED-handler redirecter til "/".
    } catch (updateError) {
      setError(updateError instanceof Error ? updateError.message : "Kunne ikke oppdatere passord");
    }
  };

  return (
    <form
      onSubmit={submit}
      className="w-full max-w-md space-y-4 rounded-lg border border-foreground/15 bg-card p-8 shadow-sm"
    >
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Sett nytt passord</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Velg et nytt passord på minst {MIN_PASSWORD_LENGTH} tegn.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="password">Nytt passord</Label>
        <Input
          id="password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={MIN_PASSWORD_LENGTH}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="confirm">Bekreft passord</Label>
        <Input
          id="confirm"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
          minLength={MIN_PASSWORD_LENGTH}
        />
      </div>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <Button type="submit" disabled={updatePassword.isPending} className="w-full">
        {updatePassword.isPending ? "Lagrer..." : "Lagre nytt passord"}
      </Button>
    </form>
  );
}
